<template>
  <Flex
    direction="column"
    fullWidth
  >
    <Flex
      align="center"
      justify="between"
    >
      <Flex
        direction="column"
        :gap=".25"
      >
        <BaseHeading size="l">
          {{ $t('register.salonized_pay.dashboard.terminals_page.heading') }}
        </BaseHeading>
        <BaseText color="secondary">
          {{ $t('register.salonized_pay.dashboard.terminals_page.subheading') }}
        </BaseText>
      </Flex>
      <BaseButton
        v-if="showAddTerminal"
        icon="plus"
        @click="$emit('addTerminal')"
        v-test="'sp-terminals-add-button'"
      >
        {{ $t('register.salonized_pay.dashboard.terminals_page.add') }}
      </BaseButton>
    </Flex>

    <BaseCard :heading="$t('global.pages.pos_terminals')">
      <EmptyPageContent
        v-if="!terminals.length && !terminalOrders.length"
        :text="{
          title: $t('register.salonized_pay.dashboard.terminals_page.empty_state.title'),
          description: $t('register.salonized_pay.dashboard.terminals_page.empty_state.description')
        }"
        v-test="'sp-dashboard-terminals-empty'"
      />
      <Terminals
        v-else
        :terminals="terminals"
        :terminalOrders="terminalOrders"
      />
    </BaseCard>

    <BaseCard
      v-if="unleash.isEnabled('SalonizedPayTerminalTipping') && hasTerminal"
      :heading="$t('register.salonized_pay.dashboard.terminals_page.tipping_heading')"
    >
      <Flex direction="column">
        <BaseToggle
          v-model="terminalTippingEnabled"
          :label="$t('admin.booking.online_payment_tipping.label')"
          v-test="'sp-terminals-tipping-switch'"
        />
        <BaseText color="secondary">
          {{ $t('register.salonized_pay.dashboard.terminals_page.tipping_description') }}
        </BaseText>

        <Flex
          v-if="terminalTippingEnabled"
          align="center"
          :gap=".5"
        >
          <BaseLabel
            v-for="(preset, index) in presetLabels"
            :key="index"
          >
            {{ preset }}
          </BaseLabel>
          <BaseText
            @click="$emit('editTipping')"
            v-test="'sp-terminals-tipping-edit'"
          >
            {{ $t('global.actions.edit') }}
          </BaseText>
        </Flex>
      </Flex>
    </BaseCard>
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';
import BaseToggle from '@/components/BaseToggle.vue';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import filters from '@/filters';
import { useAdyenStore } from '@/stores/adyen';
import type { AdyenTerminalOrder, PosTerminal } from '@/types';
import unleash from '@/unleash';
import { GET_ADYEN_TERMINAL_SETTINGS } from '../graphql';
import Terminals from './terminals/index.vue';

defineProps<{
  terminals: PosTerminal[];
  terminalOrders: AdyenTerminalOrder[];
  hasTerminal: boolean;
  showAddTerminal: boolean;
}>();

defineEmits(['addTerminal', 'editTipping']);

const { adyenAppState, updateTerminalTipping } = useAdyenStore();

const terminalTippingEnabled = computed({
  get: () => !!adyenAppState.terminalTippingEnabled,
  set: (val: boolean) => {
    updateTerminalTipping(val);
  },
});

const { result } = useQuery(GET_ADYEN_TERMINAL_SETTINGS);

const adyenStore = computed(() =>
  result.value?.adyenAppState?.adyenStores.find(
    (store: { online: boolean }) => !store.online,
  ),
);

// Raw preset strings are already display-ready: a percentage keeps its "%"
// suffix, a fixed amount is cents that just needs currency formatting.
const presetLabels = computed<string[]>(() =>
  (adyenStore.value?.predefinedTips ?? []).map((raw: string) =>
    raw.endsWith('%') ? raw : filters.currency(Number.parseInt(raw, 10)),
  ),
);
</script>
